/**
 * Cloudflare Stream Player with Google IMA Ads
 * 
 * Hybrid player that:
 * - Uses Google IMA for pre-roll and mid-roll ads
 * - Uses Cloudflare's native iframe player for content (proper pause frame)
 * - Switches between ad layer and content layer seamlessly
 */

import React, { useRef, useEffect, useState, forwardRef, useImperativeHandle, useCallback } from 'react';

const API = process.env.REACT_APP_BACKEND_URL || '';
const TEST_VAST_TAG = 'https://pubads.g.doubleclick.net/gampad/ads?iu=/21775744923/external/single_preroll_skippable&sz=640x480&ciu_szs=300x250%2C728x90&gdfp_req=1&output=vast&unviewed_position_start=1&env=vp&impl=s&correlator=';

const CloudflareStreamPlayer = forwardRef(({
  videoId,
  videoSlug, // For fetching video-specific cue points
  poster,
  customerCode = 'xg4xjbetrvqn62vp',
  autoPlay = false,
  enableAds = true,
  onPlay,
  onPause,
  onEnded,
  onTimeUpdate,
  onAdStart,
  onAdEnd,
  className = '',
  style = {},
}, ref) => {
  // Refs
  const containerRef = useRef(null);
  const iframeRef = useRef(null);
  const adVideoRef = useRef(null);
  const adContainerRef = useRef(null);
  const adsLoaderRef = useRef(null);
  const adsManagerRef = useRef(null);
  const adDisplayContainerRef = useRef(null);
  const midRollTimersRef = useRef([]);
  
  // State
  const [playerState, setPlayerState] = useState('idle'); // idle, loading, playing, paused, ad-playing
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [vastTag, setVastTag] = useState(null); // Only set when a real VAST tag is configured
  const [imaReady, setImaReady] = useState(false);
  const [preRollComplete, setPreRollComplete] = useState(false);
  const [adSettingsLoaded, setAdSettingsLoaded] = useState(false);
  const [adCuePoints, setAdCuePoints] = useState([]);
  const [playedAdCuePoints, setPlayedAdCuePoints] = useState(new Set());

  // Cloudflare Stream embed URL - start paused, we control playback
  const streamUrl = `https://customer-${customerCode}.cloudflarestream.com/${videoId}/iframe?poster=${encodeURIComponent(poster || '')}&autoplay=false&preload=auto&controls=true`;

  // Fetch ad settings (global VAST tag + ad breaks) and video-specific cue points
  useEffect(() => {
    const fetchAdSettings = async () => {
      try {
        // Fetch global ad settings
        const globalResponse = await fetch(`${API}/api/vod/admin/ads/settings`);
        if (globalResponse.ok) {
          const data = await globalResponse.json();
          
          // Set VAST tag — only use real tags, filter out Google test/sample tags
          const isTestTag = data.google_vast_tag && (
            data.google_vast_tag.includes('single_ad_samples') ||
            data.google_vast_tag.includes('single_preroll_skippable') ||
            data.google_vast_tag.includes('vmap_pre_mid_post') ||
            data.google_vast_tag.includes('external/single_') ||
            data.google_vast_tag.includes('external/vmap_')
          );
          if (data.google_vast_tag && !isTestTag) {
            setVastTag(data.google_vast_tag);
            console.log('[CloudflarePlayer] Real VAST tag loaded');
          } else if (isTestTag) {
            console.log('[CloudflarePlayer] Ignoring test/sample VAST tag');
          } else {
            console.log('[CloudflarePlayer] No VAST tag configured');
          }
          
          // Extract global ad break times
          const globalAdBreaks = [];
          for (let i = 1; i <= 6; i++) {
            const enabled = data[`ad_break_${i}_enabled`];
            const time = data[`ad_break_${i}_time`];
            if (enabled && time !== undefined && time >= 0) {
              globalAdBreaks.push(time);
              console.log(`[CloudflarePlayer] Global ad break ${i}: ${time}s`);
            }
          }
          
          // Fetch video-specific cue points
          let videoSpecificCues = [];
          let hasVideoSpecificAds = false;
          
          if (videoSlug || videoId) {
            try {
              const cueResponse = await fetch(`${API}/api/vod-admin/ad-cues/video/${videoSlug || videoId}/cue-points-for-player`);
              if (cueResponse.ok) {
                const cueData = await cueResponse.json();
                console.log('[CloudflarePlayer] Video ad settings:', cueData);
                
                hasVideoSpecificAds = cueData.has_active_ads === true;
                
                // Extract mid-roll timestamps from video-specific settings
                if (cueData.mid_rolls && Array.isArray(cueData.mid_rolls) && cueData.mid_rolls.length > 0) {
                  videoSpecificCues = cueData.mid_rolls.map(mr => mr.timestamp).filter(t => t > 0);
                  console.log('[CloudflarePlayer] Video-specific mid-roll cue points:', videoSpecificCues);
                }
              }
            } catch (err) {
              console.log('[CloudflarePlayer] Error fetching video cue points:', err);
            }
          }
          
          // Use video-specific cues if they exist, otherwise use global ad breaks
          const finalCuePoints = hasVideoSpecificAds && videoSpecificCues.length > 0 
            ? videoSpecificCues 
            : globalAdBreaks.filter(t => t > 0); // Filter out pre-roll (0) for mid-roll list
          
          setAdCuePoints(finalCuePoints);
          console.log('[CloudflarePlayer] Final mid-roll cue points:', finalCuePoints);
          
          // Check if pre-roll is enabled (ad break at time 0)
          const hasPreRoll = globalAdBreaks.includes(0) || (hasVideoSpecificAds && videoSpecificCues.includes(0));
          if (!hasPreRoll) {
            // No pre-roll, mark as complete so we go straight to content
            console.log('[CloudflarePlayer] No pre-roll ad configured');
          }
        }
      } catch (err) {
        console.log('[CloudflarePlayer] Error fetching ad settings:', err);
      } finally {
        setAdSettingsLoaded(true);
      }
    };
    
    if (enableAds) {
      fetchAdSettings();
    }
  }, [enableAds, videoSlug, videoId]);

  // Load Google IMA SDK
  useEffect(() => {
    if (!enableAds) return;
    
    if (window.google?.ima) {
      setImaReady(true);
      return;
    }
    
    const script = document.createElement('script');
    script.src = 'https://imasdk.googleapis.com/js/sdkloader/ima3.js';
    script.async = true;
    script.onload = () => {
      console.log('[CloudflarePlayer] IMA SDK loaded');
      setImaReady(true);
    };
    document.body.appendChild(script);
  }, [enableAds]);

  // Initialize IMA
  const initializeIMA = useCallback(() => {
    if (!window.google?.ima || !adVideoRef.current || !adContainerRef.current) return;
    
    console.log('[CloudflarePlayer] Initializing IMA');
    
    // Create ad display container
    adDisplayContainerRef.current = new window.google.ima.AdDisplayContainer(
      adContainerRef.current,
      adVideoRef.current
    );
    
    // Create ads loader
    adsLoaderRef.current = new window.google.ima.AdsLoader(adDisplayContainerRef.current);
    
    // Ads loader events
    adsLoaderRef.current.addEventListener(
      window.google.ima.AdsManagerLoadedEvent.Type.ADS_MANAGER_LOADED,
      onAdsManagerLoaded,
      false
    );
    
    adsLoaderRef.current.addEventListener(
      window.google.ima.AdErrorEvent.Type.AD_ERROR,
      onAdError,
      false
    );
  }, []);

  // Initialize IMA when ready
  useEffect(() => {
    if (imaReady && adVideoRef.current && adContainerRef.current) {
      initializeIMA();
    }
  }, [imaReady, initializeIMA]);

  // Play content (Cloudflare iframe) — must be defined before requestAds which references it
  const playContent = useCallback(() => {
    console.log('[CloudflarePlayer] Playing content');
    setPlayerState('playing');
    setIsPlaying(true);
    sendCommandToIframe('play');
    onPlay?.();
  }, [sendCommandToIframe, onPlay]);

  // Request ads
  const requestAds = useCallback((isPreRoll = true) => {
    if (!adsLoaderRef.current || !vastTag) {
      console.log('[CloudflarePlayer] No ads loader or VAST tag, skipping ad');
      if (isPreRoll) {
        setPreRollComplete(true);
        playContent();
      }
      return;
    }
    
    // Initialize ad display container (required before requesting ads)
    adDisplayContainerRef.current?.initialize();
    
    const adsRequest = new window.google.ima.AdsRequest();
    adsRequest.adTagUrl = vastTag;
    adsRequest.linearAdSlotWidth = containerRef.current?.clientWidth || 640;
    adsRequest.linearAdSlotHeight = containerRef.current?.clientHeight || 360;
    
    console.log('[CloudflarePlayer] Requesting ads, isPreRoll:', isPreRoll);
    adsLoaderRef.current.requestAds(adsRequest);
  }, [vastTag, playContent]);

  // Ads manager loaded
  const onAdsManagerLoaded = useCallback((adsManagerLoadedEvent) => {
    console.log('[CloudflarePlayer] Ads manager loaded');
    
    const adsRenderingSettings = new window.google.ima.AdsRenderingSettings();
    adsRenderingSettings.restoreCustomPlaybackStateOnAdBreakComplete = true;
    
    adsManagerRef.current = adsManagerLoadedEvent.getAdsManager(
      adVideoRef.current,
      adsRenderingSettings
    );
    
    const am = adsManagerRef.current;
    
    // Ad events
    am.addEventListener(window.google.ima.AdEvent.Type.CONTENT_PAUSE_REQUESTED, () => {
      console.log('[CloudflarePlayer] Ad starting - pausing content');
      sendCommandToIframe('pause');
      setPlayerState('ad-playing');
      onAdStart?.();
    });
    
    am.addEventListener(window.google.ima.AdEvent.Type.CONTENT_RESUME_REQUESTED, () => {
      console.log('[CloudflarePlayer] Ad ended - resuming content');
      setPlayerState('playing');
      setPreRollComplete(true);
      sendCommandToIframe('play');
      onAdEnd?.();
    });
    
    am.addEventListener(window.google.ima.AdEvent.Type.SKIPPED, () => {
      console.log('[CloudflarePlayer] Ad skipped');
      setPlayerState('playing');
      setPreRollComplete(true);
      sendCommandToIframe('play');
      onAdEnd?.();
    });
    
    am.addEventListener(window.google.ima.AdEvent.Type.ALL_ADS_COMPLETED, () => {
      console.log('[CloudflarePlayer] All ads completed');
      setPreRollComplete(true);
    });
    
    am.addEventListener(window.google.ima.AdErrorEvent.Type.AD_ERROR, onAdError);
    
    // Start the ad
    try {
      am.init(
        containerRef.current?.clientWidth || 640,
        containerRef.current?.clientHeight || 360,
        window.google.ima.ViewMode.NORMAL
      );
      am.start();
    } catch (err) {
      console.error('[CloudflarePlayer] Ad start error:', err);
      setPreRollComplete(true);
      playContent();
    }
  }, [onAdStart, onAdEnd]);

  // Ad error handler
  const onAdError = useCallback((adErrorEvent) => {
    console.log('[CloudflarePlayer] Ad error:', adErrorEvent.getError?.()?.getMessage?.() || 'Unknown error');
    adsManagerRef.current?.destroy();
    setPreRollComplete(true);
    setPlayerState('playing');
    sendCommandToIframe('play');
  }, []);

  // Send command to Cloudflare iframe
  const sendCommandToIframe = useCallback((command, value) => {
    if (iframeRef.current?.contentWindow) {
      iframeRef.current.contentWindow.postMessage(
        JSON.stringify({ command, value }),
        '*'
      );
    }
  }, []);

  // Handle play button click
  const handlePlay = useCallback(() => {
    console.log('[CloudflarePlayer] Play clicked, preRollComplete:', preRollComplete, 'adSettingsLoaded:', adSettingsLoaded);
    
    if (playerState === 'ad-playing') return;
    
    // Wait for ad settings to load before deciding on pre-roll
    if (enableAds && !adSettingsLoaded) {
      console.log('[CloudflarePlayer] Waiting for ad settings to load...');
      setPlayerState('loading');
      return; // Will be re-triggered by auto-play effect when settings load
    }
    
    if (enableAds && imaReady && !preRollComplete) {
      // Show pre-roll ad first
      setPlayerState('loading');
      requestAds(true);
    } else {
      playContent();
    }
  }, [enableAds, imaReady, preRollComplete, playerState, adSettingsLoaded, requestAds, playContent]);

  // Handle pause
  const handlePause = useCallback(() => {
    if (playerState === 'ad-playing') {
      adsManagerRef.current?.pause();
    } else {
      sendCommandToIframe('pause');
      setPlayerState('paused');
      setIsPlaying(false);
      onPause?.();
    }
  }, [playerState, sendCommandToIframe, onPause]);

  // Listen for messages from the Cloudflare iframe
  useEffect(() => {
    const handleMessage = (event) => {
      if (!event.origin.includes('cloudflarestream.com')) return;
      
      try {
        const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
        
        switch (data.event) {
          case 'play':
            if (playerState !== 'ad-playing') {
              setIsPlaying(true);
              setPlayerState('playing');
            }
            break;
          case 'pause':
            if (playerState !== 'ad-playing') {
              setIsPlaying(false);
              setPlayerState('paused');
              onPause?.();
            }
            break;
          case 'ended':
            setIsPlaying(false);
            setPlayerState('idle');
            onEnded?.();
            break;
          case 'timeupdate':
            if (data.currentTime !== undefined && playerState !== 'ad-playing') {
              const time = data.currentTime;
              const dur = data.duration || 0;
              setCurrentTime(time);
              setDuration(dur);
              onTimeUpdate?.(time, dur);
              
              // Check for mid-roll ad cue points
              checkMidRollAds(time);
            }
            break;
          default:
            break;
        }
      } catch (e) {
        // Ignore non-JSON messages
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [playerState, onPause, onEnded, onTimeUpdate]);

  // Check for mid-roll ads
  const checkMidRollAds = useCallback((time) => {
    if (!enableAds || playerState === 'ad-playing') return;
    
    // Don't check mid-rolls until video has started playing for at least 5 seconds
    if (time < 5) return;
    
    for (const cuePoint of adCuePoints) {
      const cueTime = typeof cuePoint === 'object' ? cuePoint.time : cuePoint;
      
      // Check if we've passed the cue point (within 2 second window) and haven't played it yet
      if (time >= cueTime && time < cueTime + 2 && !playedAdCuePoints.has(cueTime)) {
        console.log('[CloudflarePlayer] Mid-roll ad triggered at:', cueTime, 'current time:', time);
        setPlayedAdCuePoints(prev => new Set([...prev, cueTime]));
        
        // Pause content and play mid-roll ad
        sendCommandToIframe('pause');
        setPlayerState('loading');
        requestAds(false);
        break;
      }
    }
  }, [enableAds, playerState, adCuePoints, playedAdCuePoints, sendCommandToIframe, requestAds]);

  // Safety timeout: if stuck in 'loading' state for >8 seconds, force play content
  useEffect(() => {
    if (playerState !== 'loading') return;
    const safetyTimer = setTimeout(() => {
      if (playerState === 'loading') {
        console.warn('[CloudflarePlayer] Safety timeout: stuck in loading state, forcing content play');
        playContent();
      }
    }, 8000);
    return () => clearTimeout(safetyTimer);
  }, [playerState, playContent]);


  // Keyboard controls
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      
      if (e.key === ' ' || e.code === 'Space') {
        e.preventDefault();
        if (playerState === 'playing') {
          handlePause();
        } else if (playerState !== 'ad-playing') {
          handlePlay();
        }
      }
      
      if (e.key === 'f' || e.key === 'F') {
        e.preventDefault();
        if (document.fullscreenElement) {
          document.exitFullscreen();
        } else {
          containerRef.current?.requestFullscreen();
        }
      }
    };
    
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [playerState, handlePlay, handlePause]);

  // Expose methods via ref
  useImperativeHandle(ref, () => ({
    play: () => { handlePlay(); return Promise.resolve(); },
    pause: handlePause,
    seek: (time) => sendCommandToIframe('seek', time),
    getCurrentTime: () => currentTime,
    currentTime: currentTime,
    getDuration: () => duration,
    duration: duration,
    isPlaying: () => isPlaying,
  }), [handlePlay, handlePause, sendCommandToIframe, currentTime, duration, isPlaying]);

  // Auto-play on mount if enabled (wait for ad settings to load)
  useEffect(() => {
    if (autoPlay && playerState === 'idle' && (!enableAds || adSettingsLoaded)) {
      // Small delay to ensure everything is mounted
      const timer = setTimeout(() => {
        handlePlay();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [autoPlay, playerState, handlePlay, enableAds, adSettingsLoaded]);

  // Re-trigger play when ad settings finish loading (if player was waiting)
  useEffect(() => {
    if (adSettingsLoaded && playerState === 'loading' && enableAds) {
      console.log('[CloudflarePlayer] Ad settings loaded, re-triggering play');
      handlePlay();
    }
  }, [adSettingsLoaded, playerState, enableAds, handlePlay]);


  const isAdPlaying = playerState === 'ad-playing';
  const isLoading = playerState === 'loading';
  const isIdle = playerState === 'idle';

  return (
    <div
      ref={containerRef}
      className={`cloudflare-stream-player ${className}`}
      tabIndex={0}
      style={{
        position: 'relative',
        width: '100%',
        height: '100%',
        backgroundColor: '#000',
        outline: 'none',
        ...style,
      }}
    >
      {/* Cloudflare Stream iframe - main content player */}
      <iframe
        ref={iframeRef}
        src={streamUrl}
        title="Video Player"
        allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture"
        allowFullScreen
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          border: 'none',
          opacity: isAdPlaying ? 0 : 1,
          pointerEvents: isAdPlaying ? 'none' : 'auto',
          zIndex: 1,
        }}
      />
      
      {/* Ad video element - for Google IMA */}
      <video
        ref={adVideoRef}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          display: isAdPlaying ? 'block' : 'none',
          zIndex: 10,
        }}
        playsInline
      />
      
      {/* Ad container - IMA renders controls here */}
      <div
        ref={adContainerRef}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          display: isAdPlaying ? 'block' : 'none',
          zIndex: 15,
        }}
      />
      
      {/* Ad badge */}
      {isAdPlaying && (
        <div style={{
          position: 'absolute',
          top: 16,
          left: 16,
          backgroundColor: '#f59e0b',
          color: '#000',
          padding: '4px 12px',
          borderRadius: 20,
          fontSize: 12,
          fontWeight: 'bold',
          zIndex: 20,
        }}>
          Ad
        </div>
      )}
      
      {/* Loading spinner */}
      {isLoading && (
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: 20,
        }}>
          <div style={{
            width: 48,
            height: 48,
            border: '4px solid rgba(255,255,255,0.3)',
            borderTop: '4px solid white',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
          }} />
          <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
        </div>
      )}
      
      {/* Initial play button overlay */}
      {isIdle && !isLoading && (
        <div
          onClick={handlePlay}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            zIndex: 5,
            backgroundColor: 'rgba(0,0,0,0.3)',
          }}
        >
          <div style={{
            width: 80,
            height: 80,
            backgroundColor: 'rgba(139,92,246,0.9)',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <svg width="36" height="36" viewBox="0 0 24 24" fill="white">
              <path d="M8 5v14l11-7z"/>
            </svg>
          </div>
        </div>
      )}
    </div>
  );
});

CloudflareStreamPlayer.displayName = 'CloudflareStreamPlayer';

export default CloudflareStreamPlayer;
